import { Injectable, inject, signal } from '@angular/core';
import { FeatureEntity } from '@gishub/domain';
import { ArcGisQueryRepositoryProvider } from '../../shared/providers/repository-providers';
import { LocalInterventionService, filterLocalByNames } from './local-intervention-service';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import Polygon from '@arcgis/core/geometry/Polygon';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';

const INTERVENCIONES_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/6';
const CONCESIONES_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/29';
const PREDIOS_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/12';
const PROVINCE_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/34';
const DISTRICT_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/33';
const COMPROMISOS_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/1';
const CONFLICTOS_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/2';
const ANP_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/19';
const CUM_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/14';
const AG_SURFACE_URL = 'https://winlmprap09.midagri.gob.pe/winlmprap14/rest/services/ideMidagri/DGESEP/MapServer/2';
const BASIN_URL = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services/DGIHR/Monitoreo_Territorial_Junin/MapServer/18';

// ── Interfaces de datos ──

export interface MiningConcessionRecord {
  concesion: string;
  titular: string;
  estado: string;
  sustancia: string;
  hectareas: number;
  codigo: string;
  ubicacion: string;
  observacion: string;
  leyenda: string;
  geometry?: any;
}

export interface PredioRecord {
  codigo: string;
  numero: string;
  areaHa: number;
  observacion: string;
  origen: string;
  geometry?: any;
}

export interface InterventionRecord {
  dependencia_responsable: string;
  tipo_intervencion: string;
  nombre_intervencion: string;
  servicio_publico: string;
  ubicacion: string;
  region: string;
  provincia: string;
  distrito: string;
  cui: string;
  comentario: string;
  fase: string;
  num_comunidades: number;
  beneficiarios: number;
  presupuesto: number;
  meta: string;
  und_medida: string;
  comentario_observacion: string;
  nombre_comunidad_beneficiaria: string;
  descr: string;
  fecha_inicio: string;
  fecha_termino: string;
  geometry?: any;
}

export interface CommitmentRecord {
  nomcorto: string;
  origen_conflicto: string;
  espacio_dialogo: string;
  nombre_proyecto_actividad: string;
  tipo_compromiso: string;
  cui: string;
  unidad_formuladora: string;
  unidad_ejecutora: string;
  fase_pi: string;
  costo_pi_estimado: string;
  estado_actual: string;
  estado_compromiso: string;
  responsable: string;
  geometry?: any;
}

export interface SocialConflictRecord {
  ubicacion: string;
  tipo: string;
  caso: string;
  actores_primarios: string;
  actores_secundarios: string;
  actores_terciarios: string;
  estado_actual: string;
  observaciones: string;
  estado: string;
  dialogo: string;
  situacion: string;
  solucion: string;
  geometry?: any;
}

export interface ANPRecord {
  anp_codi: string;
  anp_cate: string;
  anp_nomb: string;
  anp_sect: string;
  anp_ubpo: string;
  anp_suleg: number;
  anp_uicn: string;
  anp_obs: string;
  geometry?: any;
}

export interface CUMRecord {
  grupocum: string;
  simcum: string;
  descrip: string;
  propor: string;
  ha: number;
  geometry?: any;
}

export interface AgSurfaceRecord {
  categoria: string;
  area_ha: number;
  zona_dist: string;
  fuente: string;
  geometry?: any;
}

export interface BasinRecord {
  nombre: string;
  geometry?: any;
}

export interface CommunityProperty {
  label: string;
  value: string;
  icon: string;
  category: 'ubicacion' | 'identidad' | 'demografia' | 'servicios';
}

export interface CommunityProfile {
  name: string;
  location: string;
  type: string;
  properties: CommunityProperty[];
  interventions: InterventionRecord[];
  miningConcessions: MiningConcessionRecord[];
  predios: PredioRecord[];
  commitments: CommitmentRecord[];
  socialConflicts: SocialConflictRecord[];
  anps: ANPRecord[];
  cums: CUMRecord[];
  agSurfaces: AgSurfaceRecord[];
  basins: BasinRecord[];
}

@Injectable({ providedIn: 'root' })
export class CommunityProfileService {
  private queryRepo = inject(ArcGisQueryRepositoryProvider).instance;
  private readonly _localInterventions = inject(LocalInterventionService);

  readonly profile = signal<CommunityProfile | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  async compute(feature: FeatureEntity): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const geometry = this._toArcGISGeom(feature.geometry);
      if (!geometry) {
        this.error.set('No se pudo obtener la geometría de la comunidad');
        this.loading.set(false);
        return;
      }

      const attrs = feature.attributes as Record<string, any> || {};
      const name = this._extractName(attrs);
      const parts = [
        String(attrs['nombdep'] || attrs['DEPARTAMENTO'] || attrs['nom_dpto'] || ''),
        String(attrs['nombprov'] || attrs['PROVINCIA'] || attrs['nom_prov'] || ''),
        String(attrs['nombdist'] || attrs['distrito'] || attrs['nom_dist'] || ''),
      ].filter(Boolean);
      const location = parts.length > 0 ? parts.join(' / ') : '';
      const type = this._detectType(feature);

      let properties = this._extractProperties(attrs);

      // Enrich Ubicación with province/district from spatial layers if missing
      if (geometry && (!properties.some(p => p.label === 'Provincia') || !properties.some(p => p.label === 'Distrito'))) {
        try {
          const enriched = await this._enrichUbicacion(properties, geometry);
          if (enriched) properties = enriched;
        } catch { /* spatial enrichment failed, use whatever attributes we have */ }
      }

      const [interventions, interventionsDetail, miningConcessions, predios, commitments, socialConflicts, anps, cums, agSurfaces, basins] = await Promise.all([
        this._loadInterventions(attrs).catch(() => ({ title: 'Intervenciones del Estado', icon: '🏛️', items: [] })),
        this._loadInterventionsDetail(feature).catch(() => [] as InterventionRecord[]),
        this._loadMiningConcessions(feature).catch(() => [] as MiningConcessionRecord[]),
        this._loadPredios(feature).catch(() => [] as PredioRecord[]),
        this._loadCommitments(feature).catch(() => [] as CommitmentRecord[]),
        this._loadSocialConflicts(feature).catch(() => [] as SocialConflictRecord[]),
        this._loadANPs(feature).catch(() => [] as ANPRecord[]),
        this._loadCUMs(feature).catch(() => [] as CUMRecord[]),
        this._loadAgSurfaces(feature).catch(() => [] as AgSurfaceRecord[]),
        this._loadBasins(feature).catch(() => [] as BasinRecord[]),
      ]);

      this.profile.set({ name, location, type, properties, interventions: interventionsDetail, miningConcessions, predios, commitments, socialConflicts, anps, cums, agSurfaces, basins });
    } catch (e) {
      console.error('[CommunityProfile]', e);
      this.error.set('Error al generar el perfil');
    } finally {
      this.loading.set(false);
    }
  }

  clear(): void {
    this.profile.set(null);
    this.loading.set(false);
    this.error.set(null);
  }

  // ── Extraer propiedades de la comunidad ──

  private _extractProperties(attrs: Record<string, any>): CommunityProperty[] {
    const props: CommunityProperty[] = [];
    const map: [string, string, string, 'ubicacion' | 'identidad' | 'demografia' | 'servicios'][] = [
      ['nombdep', 'Departamento', '📍', 'ubicacion'],
      ['DEPARTAMENTO', 'Departamento', '📍', 'ubicacion'],
      ['nom_dpto', 'Departamento', '📍', 'ubicacion'],
      ['nombprov', 'Provincia', '📍', 'ubicacion'],
      ['PROVINCIA', 'Provincia', '📍', 'ubicacion'],
      ['nom_prov', 'Provincia', '📍', 'ubicacion'],
      ['nombdist', 'Distrito', '📍', 'ubicacion'],
      ['distrito', 'Distrito', '📍', 'ubicacion'],
      ['nom_dist', 'Distrito', '📍', 'ubicacion'],
      ['region', 'Región', '📍', 'ubicacion'],
      ['ccpp', 'Centro Poblado', '🏘️', 'ubicacion'],
      ['centro_poblado', 'Centro Poblado', '🏘️', 'ubicacion'],
      ['localidad', 'Localidad', '🏘️', 'ubicacion'],
      ['federacion', 'Federación', '🤝', 'identidad'],
      ['ambito_pueblo_indigena', 'Ámbito Pueblo Indígena', '🏳️', 'identidad'],
      ['ambito', 'Ámbito', '🏳️', 'identidad'],
      ['pueblo_indigena', 'Pueblo Indígena u Originario', '🏳️', 'identidad'],
      ['pueblo_originario', 'Pueblo Indígena u Originario', '🏳️', 'identidad'],
      ['comunidad_nativa', 'Comunidad Nativa', '🏳️', 'identidad'],
      ['etnia', 'Etnia', '🏳️', 'identidad'],
      ['pct_lengua_indigena', '% Lengua Indígena', '🗣️', 'demografia'],
      ['porc_lengua_indigena', '% Lengua Indígena', '🗣️', 'demografia'],
      ['pct_masculino', '% Población Masculina', '👨', 'demografia'],
      ['porc_masculino', '% Población Masculina', '👨', 'demografia'],
      ['pct_femenino', '% Población Femenina', '👩', 'demografia'],
      ['porc_femenino', '% Población Femenina', '👩', 'demografia'],
      ['poblacion_total', 'Población Total', '👥', 'demografia'],
      ['poblacion', 'Población Total', '👥', 'demografia'],
      ['total_poblacion', 'Población Total', '👥', 'demografia'],
      ['familias', 'Familias', '👨‍👩‍👧‍👦', 'demografia'],
      ['viviendas', 'Viviendas', '🏠', 'demografia'],
      ['pct_sin_agua', '% Viviendas sin Agua', '🚱', 'servicios'],
      ['porc_sin_agua', '% Viviendas sin Agua', '🚱', 'servicios'],
      ['sin_agua_pct', '% Viviendas sin Agua', '🚱', 'servicios'],
      ['pct_sin_desague', '% Viviendas sin Desagüe', '🚽', 'servicios'],
      ['porc_sin_desague', '% Viviendas sin Desagüe', '🚽', 'servicios'],
      ['sin_desague_pct', '% Viviendas sin Desagüe', '🚽', 'servicios'],
      ['pct_sin_electricidad', '% Viviendas sin Electricidad', '⚡', 'servicios'],
      ['porc_sin_electricidad', '% Viviendas sin Electricidad', '⚡', 'servicios'],
      ['sin_electricidad_pct', '% Viviendas sin Electricidad', '⚡', 'servicios'],
      ['pct_sin_agua_potable', '% Viviendas sin Agua Potable', '🚱', 'servicios'],
      ['pct_no_agua', '% Viviendas sin Agua', '🚱', 'servicios'],
      ['pct_no_desague', '% Viviendas sin Desagüe', '🚽', 'servicios'],
      ['pct_no_electricidad', '% Viviendas sin Electricidad', '⚡', 'servicios'],
      // ── MapServer/10 (Comunidad Nativa) ──
      ['poblacion_total_aprox', 'Población Total Aprox', '👥', 'demografia'],
      ['poblacion_masculina_aprox', 'Población Masculina Aprox', '👨', 'demografia'],
      ['poblacion_femenina_aprox', 'Población Femenina Aprox', '👩', 'demografia'],
      ['poblacion_que_habla_alguna_lengua_indigena_u_originaria_aprox', 'Hablantes Lengua Indígena Aprox', '🗣️', 'demografia'],
      ['pct_de_poblacion_masculina', '% Población Masculina', '👨', 'demografia'],
      ['pct_de_poblacion_femenina', '% Población Femenina', '👩', 'demografia'],
      ['pct_de_poblacion_que_habla_alguna_lengua_indigena_u_originaria', '% Lengua Indígena', '🗣️', 'demografia'],
      ['poblacion_que_se_autoidentifica_como_parte_de_algun_pueblo_indig', 'Autoidentif. Pueblo Indígena', '🏳️', 'demografia'],
      ['pct_de_poblacion_que_se_autoidentifica_como_parte_de_algun_puebl', '% Autoidentif. Pueblo Indígena', '🏳️', 'demografia'],
      ['poblacion_hablante_de_3_anos_a_mas_aprox', 'Hablantes 3+ años Aprox', '🗣️', 'demografia'],
      ['poblacion_de_12_anos_a_mas_aprox', 'Población 12+ años Aprox', '👥', 'demografia'],
      ['num_de_hogares_con_poblacion_en_el_centro_poblado_aprox', 'Hogares C.P. Aprox', '👨‍👩‍👧‍👦', 'demografia'],
      ['num_de_viviendas_con_poblacion_aprox', 'Viviendas con Población', '🏠', 'demografia'],
      ['num_de_viviendas_sin_servicio_de_agua_potable_aprox', 'Viviendas sin Agua Potable', '🚱', 'servicios'],
      ['pct_de_viviendas_sin_servicio_de_agua_potable', '% Viviendas sin Agua Potable', '🚱', 'servicios'],
      ['num_de_viviendas_sin_servicio_de_desage_aprox', 'Viviendas sin Desagüe', '🚽', 'servicios'],
      ['pct_de_viviendas_sin_servicio_de_desage', '% Viviendas sin Desagüe', '🚽', 'servicios'],
      ['num_de_viviendas_sin_servicio_de_electricidad_aprox', 'Viviendas sin Electricidad', '⚡', 'servicios'],
      ['pct_de_viviendas_sin_servicio_de_electricidad', '% Viviendas sin Electricidad', '⚡', 'servicios'],
      ['pueblo_indigena_u_originario_de_la_localidad', 'Pueblo Indígena u Originario', '🏳️', 'identidad'],
      ['ambito_del_pueblo_indigena_u_originario', 'Ámbito Pueblo Indígena', '🏳️', 'identidad'],
      ['nro_titulo', 'N° Título', '📜', 'identidad'],
      ['anio_titulo', 'Año Título', '📅', 'identidad'],
      ['area_titulo', 'Área Título (ha)', '📐', 'identidad'],
      ['categoria_de_centro_poblado', 'Categoría C.P.', '🏘️', 'ubicacion'],
      ['tipo_administativo', 'Tipo Administrativo', '🏛️', 'ubicacion'],
      ['localidades_asociadas_al_centro_poblado', 'Localidades Asociadas', '🏘️', 'ubicacion'],
      // ── MapServer/9 (Comunidad Campesina) ──
      ['estado', 'Estado', '📋', 'identidad'],
      ['fuente', 'Fuente', '📂', 'identidad'],
      ['Shape_Area', 'Área (ha)', '📐', 'identidad'],
    ];

    const seen = new Set<string>();
    for (const [key, label, icon, category] of map) {
      const val = attrs[key];
      if (val != null && String(val).trim() !== '' && !seen.has(label)) {
        seen.add(label);
        let displayVal = String(val).trim();
        if (label.startsWith('%') && !displayVal.includes('%')) {
          const num = parseFloat(displayVal);
          displayVal = !isNaN(num) ? `${num.toFixed(2)}%` : `${displayVal}%`;
        }
        props.push({ label, value: displayVal, icon, category });
      }
    }

    // Convert Shape_Area from m² to ha
    const areaProp = props.find(p => p.label === 'Área (ha)');
    if (areaProp) {
      const num = parseFloat(areaProp.value);
      if (!isNaN(num)) {
        areaProp.value = (num / 10000).toLocaleString('es-PE', { maximumFractionDigits: 2 }) + ' ha';
      }
    }

    return props;
  }

  private async _enrichUbicacion(properties: CommunityProperty[], geometry: Polygon | Polyline | Point): Promise<CommunityProperty[] | null> {
    let changed = false;
    const result = [...properties];
    const hasProv = properties.some(p => p.label === 'Provincia');
    const hasDist = properties.some(p => p.label === 'Distrito');

    if (!hasDist) {
      try {
        const res = await this.queryRepo.queryLayerFeatures(DISTRICT_URL, '1=1', geometry, 'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 1, false);
        if (res.success && res.data && res.data.length > 0) {
          const a = res.data[0].attributes;
          const distName = String(a['NOMBDIST'] ?? '').trim();
          const provName = String(a['NOMBPROV'] ?? '').trim();
          if (provName && !hasProv) { result.push({ label: 'Provincia', value: provName, icon: '📍', category: 'ubicacion' }); changed = true; }
          if (distName) { result.push({ label: 'Distrito', value: distName, icon: '📍', category: 'ubicacion' }); changed = true; }
        }
      } catch { /* district query failed */ }
    }

    if (!hasProv && !changed) {
      try {
        const res = await this.queryRepo.queryLayerFeatures(PROVINCE_URL, '1=1', geometry, 'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 1, false);
        if (res.success && res.data && res.data.length > 0) {
          const a = res.data[0].attributes;
          const provName = String(a['NOMBPROV'] ?? '').trim();
          if (provName) { result.push({ label: 'Provincia', value: provName, icon: '📍', category: 'ubicacion' }); changed = true; }
        }
      } catch { /* province query failed */ }
    }

    return changed ? result : null;
  }

  // ── Carga de intervenciones ──

  private async _loadInterventionsDetail(feature: FeatureEntity): Promise<InterventionRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    // Inversiones locales (Chupaca) que correspondan al ámbito de la comunidad
    const attrs = feature.attributes as Record<string, any> || {};
    const cRegion = String(attrs['nombdep'] || attrs['DEPARTAMENTO'] || attrs['nom_dpto'] || '').trim();
    const cProv = String(attrs['nombprov'] || attrs['PROVINCIA'] || attrs['nom_prov'] || '').trim();
    const cDist = String(attrs['nombdist'] || attrs['distrito'] || attrs['nom_dist'] || '').trim();
    const localFeatures = filterLocalByNames(this._localInterventions.getLocalFeatures(), {
      region: cRegion || null,
      provincia: cProv || null,
      distrito: cDist || null,
    });

    const res = await this.queryRepo.queryLayerFeatures(
      INTERVENCIONES_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) {
      return localFeatures.map(f => this._toInterventionRecord(f));
    }

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return [
      ...localFeatures.map(f => this._toInterventionRecord(f)),
      ...intersecting.map(f => this._toInterventionRecord(f)),
    ];
  }

  private _toInterventionRecord(f: any): InterventionRecord {
    const a = f.attributes ?? {};
    const arcGeom = this._toArcGISGeom(f.geometry);
    return {
      dependencia_responsable: String(a['dependencia_responsable'] ?? '—').trim(),
      tipo_intervencion: String(a['tipo_intervencion'] ?? '—').trim(),
      nombre_intervencion: String(a['nombre_intervencion'] ?? a['nom_intervencion'] ?? '—').trim(),
      servicio_publico: String(a['servicio_publico'] ?? a['servicio_publico_intervencion'] ?? '—').trim(),
      region: String(a['region'] ?? '—').trim(),
      provincia: String(a['provincia'] ?? '—').trim(),
      distrito: String(a['distrito'] ?? '—').trim(),
      ubicacion: [String(a['region'] ?? ''), String(a['provincia'] ?? ''), String(a['distrito'] ?? '')].filter(Boolean).join(' / ') || '—',
      cui: String(a['cui'] ?? a['CUI'] ?? '—').trim(),
      comentario: String(a['comentario'] ?? a['observacion'] ?? a['observaciones'] ?? '—').trim(),
      fase: String(a['fase_pi'] ?? a['fase'] ?? '—').trim(),
      num_comunidades: Number(a['num_comunidades'] ?? a['numero_comunidades'] ?? 0),
      beneficiarios: Number(a['beneficiarios'] ?? 0),
      presupuesto: Number(a['presupuesto'] ?? 0),
      meta: String(a['meta'] ?? '—').trim(),
      und_medida: String(a['und_medida'] ?? a['unidad_medida'] ?? '—').trim(),
      comentario_observacion: String(a['comentario_observacion'] ?? '—').trim(),
      nombre_comunidad_beneficiaria: String(a['nombre_comunidad_beneficiaria'] ?? '—').trim(),
      descr: String(a['descr'] ?? a['descripcion'] ?? '—').trim(),
      fecha_inicio: String(a['fecha_inicio'] ?? '—').trim(),
      fecha_termino: String(a['fecha_termino'] ?? '—').trim(),
      geometry: arcGeom,
    };
  }

  // ── Carga de concesiones mineras ──

  private async _loadMiningConcessions(feature: FeatureEntity): Promise<MiningConcessionRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      CONCESIONES_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        concesion: String(a['CONCESION'] ?? '—').trim(),
        titular: String(a['TIT_CONCES'] ?? '—').trim(),
        estado: String(a['D_ESTADO'] ?? a['ESTADO'] ?? '—').trim(),
        sustancia: String(a['SUSTANCIA'] ?? '—').trim(),
        hectareas: Number(a['HECTAGIS'] ?? 0),
        codigo: String(a['CODIGOU'] ?? '—').trim(),
        ubicacion: [String(a['DEPA'] ?? ''), String(a['PROVI'] ?? ''), String(a['DISTRI'] ?? '')].filter(Boolean).join(' / ') || '—',
        observacion: String(a['OBSERV'] ?? '—').trim(),
        leyenda: String(a['LEYENDA'] ?? '—').trim(),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga de predios catastrales ──

  private async _loadPredios(feature: FeatureEntity): Promise<PredioRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      PREDIOS_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        codigo: String(a['cod_predio'] ?? a['cod_predio_sset'] ?? '—').trim(),
        numero: String(a['num_predio'] ?? '—').trim(),
        areaHa: Number(a['area_ha'] ?? 0),
        observacion: String(a['observacion'] ?? '—').trim(),
        origen: String(a['origen'] ?? '—').trim(),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga de compromisos agrarios ──

  private async _loadCommitments(feature: FeatureEntity): Promise<CommitmentRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      COMPROMISOS_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        nomcorto: String(a['nomcorto'] ?? '—').trim(),
        origen_conflicto: String(a['origen_conflicto'] ?? '—').trim(),
        espacio_dialogo: String(a['espacio_dialogo'] ?? '—').trim(),
        nombre_proyecto_actividad: String(a['nombre_proyecto_actividad'] ?? '—').trim(),
        tipo_compromiso: String(a['tipo_compromiso'] ?? '—').trim(),
        cui: String(a['cui'] ?? '—').trim(),
        unidad_formuladora: String(a['unidad_formuladora'] ?? '—').trim(),
        unidad_ejecutora: String(a['unidad_ejecutora'] ?? '—').trim(),
        fase_pi: String(a['fase_pi'] ?? '—').trim(),
        costo_pi_estimado: String(a['costo_pi_estimado'] ?? '—').trim(),
        estado_actual: String(a['estado_actual'] ?? '—').trim(),
        estado_compromiso: String(a['estado_compromiso'] ?? '—').trim(),
        responsable: String(a['responsable'] ?? '—').trim(),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga de conflictos sociales ──

  private async _loadSocialConflicts(feature: FeatureEntity): Promise<SocialConflictRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      CONFLICTOS_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        ubicacion: String(a['ubicacion'] ?? '—').trim(),
        tipo: String(a['tipo'] ?? '—').trim(),
        caso: String(a['caso'] ?? '—').trim(),
        actores_primarios: String(a['actores_primarios'] ?? '—').trim(),
        actores_secundarios: String(a['actores_secundarios'] ?? '—').trim(),
        actores_terciarios: String(a['actores_terciarios'] ?? '—').trim(),
        estado_actual: String(a['estado_actual'] ?? '—').trim(),
        observaciones: String(a['observaciones'] ?? '—').trim(),
        estado: String(a['estado'] ?? '—').trim(),
        dialogo: String(a['dialogo'] ?? '—').trim(),
        situacion: String(a['situacion'] ?? '—').trim(),
        solucion: String(a['solucion'] ?? '—').trim(),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga de Áreas Naturales Protegidas ──

  private async _loadANPs(feature: FeatureEntity): Promise<ANPRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      ANP_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        anp_codi: String(a['anp_codi'] ?? '—').trim(),
        anp_cate: String(a['anp_cate'] ?? '—').trim(),
        anp_nomb: String(a['anp_nomb'] ?? '—').trim(),
        anp_sect: String(a['anp_sect'] ?? '—').trim(),
        anp_ubpo: String(a['anp_ubpo'] ?? '—').trim(),
        anp_suleg: Number(a['anp_suleg'] ?? 0),
        anp_uicn: String(a['anp_uicn'] ?? '—').trim(),
        anp_obs: String(a['anp_obs'] ?? '—').trim(),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga de CUM ──

  private async _loadCUMs(feature: FeatureEntity): Promise<CUMRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      CUM_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        grupocum: String(a['grupocum'] ?? '—').trim(),
        simcum: String(a['simcum'] ?? '—').trim(),
        descrip: String(a['descrip'] ?? '—').trim(),
        propor: String(a['propor'] ?? '—').trim(),
        ha: Number(a['ha'] ?? 0),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga de superficie agropecuaria ──

  private async _loadAgSurfaces(feature: FeatureEntity): Promise<AgSurfaceRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      AG_SURFACE_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        categoria: String(a['categoria'] ?? '—').trim(),
        area_ha: Number(a['area_ha'] ?? 0),
        zona_dist: String(a['zona_dist'] ?? '—').trim(),
        fuente: String(a['fuente'] ?? '—').trim(),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga de cuencas hidrográficas ──

  private async _loadBasins(feature: FeatureEntity): Promise<BasinRecord[]> {
    const communityGeom = this._toArcGISGeom(feature.geometry);
    if (!communityGeom) return [];

    const res = await this.queryRepo.queryLayerFeatures(
      BASIN_URL, '1=1', communityGeom,
      'esriGeometryPolygon', 'esriSpatialRelIntersects', '*', true, 0, 2000, false,
    );
    if (!res.success || !res.data || res.data.length === 0) return [];

    const intersecting = res.data.filter(f => {
      if (!f.geometry) return false;
      try {
        const fGeom = this._toArcGISGeom(f.geometry);
        return fGeom ? geometryEngine.intersects(communityGeom, fGeom) : false;
      } catch { return false; }
    });

    return intersecting.map((f: any) => {
      const a = f.attributes ?? {};
      const arcGeom = this._toArcGISGeom(f.geometry);
      return {
        nombre: String(a['nombre'] ?? '—').trim(),
        geometry: arcGeom,
      };
    });
  }

  // ── Carga agregada de intervenciones (estadísticas) ──

  private async _loadInterventions(attrs: Record<string, any>): Promise<{ title: string; icon: string; items: any[] }> {
    const name = this._extractName(attrs);
    if (!name) return { title: 'Intervenciones del Estado', icon: '🏛️', items: [] };

    const escaped = name.replace(/'/g, "''").toUpperCase();
    const dpto = String(attrs['nombdep'] || attrs['DEPARTAMENTO'] || attrs['nom_dpto'] || '').replace(/'/g, "''").toUpperCase();
    let where = `UPPER(nombre_comunidad_beneficiaria) LIKE '%${escaped}%'`;
    if (dpto) where += ` AND UPPER(region) LIKE '%${dpto}%'`;

    const res = await this.queryRepo.queryLayer(INTERVENCIONES_URL, where, '*', false);

    // Inversiones locales del ámbito de la comunidad (Chupaca)
    const cDpto = String(attrs['nombdep'] || attrs['DEPARTAMENTO'] || attrs['nom_dpto'] || '').trim();
    const cProv = String(attrs['nombprov'] || attrs['PROVINCIA'] || attrs['nom_prov'] || '').trim();
    const cDist = String(attrs['nombdist'] || attrs['distrito'] || attrs['nom_dist'] || '').trim();
    const localFeatures = filterLocalByNames(this._localInterventions.getLocalFeatures(), {
      region: cDpto || null,
      provincia: cProv || null,
      distrito: cDist || null,
    });

    const features = [
      ...localFeatures,
      ...((res.success && res.data) ? res.data : []),
    ];
    if (features.length === 0) return { title: 'Intervenciones del Estado', icon: '🏛️', items: [] };

    const total = features.length;
    const totalComunidades = features.reduce((s, f) => s + (Number(f.attributes['num_comunidades'] ?? f.attributes['numero_comunidades']) || 0), 0);
    const byType = this._groupCount(features, 'tipo_intervencion');
    const byDependency = this._groupCount(features, 'dependencia_responsable');
    const byPhase = this._groupCount(features, 'fase_pi');

    const items: any[] = [];
    items.push({ label: 'Total intervenciones', value: total.toLocaleString('es-PE') });
    if (totalComunidades > 0) items.push({ label: 'Comunidades beneficiadas', value: totalComunidades.toLocaleString('es-PE') });
    if (byType.length > 0) items.push({ label: 'Por tipo', value: byType.reduce((s, [_, c]) => s + c, 0).toLocaleString('es-PE'), categories: byType.map(([n, c]) => ({ label: n, count: c, pct: Math.round(c / total * 100) })) });
    if (byDependency.length > 0) items.push({ label: 'Por dependencia', value: byDependency.reduce((s, [_, c]) => s + c, 0).toLocaleString('es-PE'), categories: byDependency.map(([n, c]) => ({ label: n, count: c, pct: Math.round(c / total * 100) })) });
    if (byPhase.length > 0) items.push({ label: 'Por fase', value: byPhase.reduce((s, [_, c]) => s + c, 0).toLocaleString('es-PE'), categories: byPhase.map(([n, c]) => ({ label: n, count: c, pct: Math.round(c / total * 100) })) });
    return { title: 'Intervenciones del Estado', icon: '🏛️', items };
  }

  private _groupCount(features: any[], field: string): [string, number][] {
    const map = new Map<string, number>();
    for (const f of features) {
      const val = f.attributes?.[field];
      const key = val != null ? String(val).trim() : 'Sin asignar';
      if (!key) continue;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }

  private _groupCountArr(records: any[], field: string): [string, number][] {
    const map = new Map<string, number>();
    for (const r of records) {
      const val = (r as any)[field];
      const key = val != null ? String(val).trim() : 'Sin asignar';
      if (!key) continue;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }

  private _ensureBuffer(geom: any, meters: number): any {
    if (!geom) return geom;
    if (geom.type === 'point' || geom.type === 'Point' || geom.x != null) {
      return geometryEngine.geodesicBuffer(geom, meters, 'meters');
    }
    return geom;
  }

  private _extractName(attrs: Record<string, any>): string {
    const keys = ['nom_comunidad', 'nombccpp', 'nomb_cp', 'nomb_cn', 'comunidad', 'nombre', 'name', 'nomb_cc', 'centro_poblado'];
    for (const k of keys) {
      const v = attrs[k];
      if (v != null && typeof v === 'string' && v.length > 1) return v;
    }
    const found = Object.values(attrs).find(v => v != null && typeof v === 'string' && v.length > 3);
    return String(found ?? 'Comunidad');
  }

  private _detectType(feature: FeatureEntity): string {
    const url = (feature as any)._url || '';
    const match = url.match(/MapServer\/(\d+)/);
    if (match) {
      const id = parseInt(match[1], 10);
      if (id === 9) return 'Comunidad campesina';
      if (id === 10) return 'Comunidad nativa';
      if (id === 32) return 'Centro poblado';
    }
    if (url.includes('Campesina') || url.includes('campesinas')) return 'Comunidad campesina';
    if (url.includes('Nativa') || url.includes('nativas')) return 'Comunidad nativa';
    if (url.includes('Centro_Poblado') || url.includes('Centros_Poblados') || url.includes('cpoblados')) return 'Centro poblado';
    return 'Comunidad';
  }

  private _toArcGISGeom(geom: any): Polygon | Polyline | Point | null {
    if (!geom) return null;
    const isGeoJson = geom.type != null && geom.coordinates != null && geom.x == null && geom.paths == null && geom.rings == null;
    try {
      if (isGeoJson) {
        const sr = geom.spatialReference ?? { wkid: 4326 };
        if (geom.type === 'Point' || geom.type === 'MultiPoint') {
          const coords = geom.type === 'MultiPoint' ? geom.coordinates[0] : geom.coordinates;
          return new Point({ x: coords[0], y: coords[1], spatialReference: sr });
        }
        if (geom.type === 'Polygon') {
          const rings = geom.coordinates.map((ring: number[][]) => ring.map((c: number[]) => [c[0], c[1]]));
          return Polygon.fromJSON({ rings, spatialReference: sr });
        }
        if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
          const paths = geom.type === 'MultiLineString' ? geom.coordinates : [geom.coordinates];
          return Polyline.fromJSON({ paths, spatialReference: sr });
        }
        return null;
      }
      const sr = geom.spatialReference ?? { wkid: 4326 };
      if (geom.rings) return Polygon.fromJSON({ rings: geom.rings, spatialReference: sr });
      if (geom.paths) return Polyline.fromJSON({ paths: geom.paths, spatialReference: sr });
      if (geom.x != null) return new Point({ x: geom.x, y: geom.y, spatialReference: sr });
      return null;
    } catch {
      return null;
    }
  }
}